import { PACK_PRICING, type SkuHandle } from "@/lib/checkout-pricing";
import { packPricingFromOverride } from "@/lib/productPageContent";
/**
 * Product page editor — server functions.
 * Public: read the published override for a handle (used by the PDP loader).
 * Admin: read/save the draft, publish it, discard it. Admin-only.
 */
import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  sanitizeOverride,
  EDITABLE_HANDLES,
  type ProductPageOverride,
} from "@/lib/productPageContent";

export type ProductPageRow = {
  handle: string;
  draft: ProductPageOverride;
  published: ProductPageOverride | null;
  published_at: string | null;
  updated_at: string | null;
};

export const getPublishedProductPage = createServerFn({ method: "GET" })
  .inputValidator((data: { handle: string }) => ({ handle: String(data.handle) }))
  .handler(async ({ data }): Promise<ProductPageOverride | null> => {
    const { loadPublishedProductPages } = await import("@/lib/product-page.server");
    const map = await loadPublishedProductPages();
    return map[data.handle] ?? null;
  });

async function assertAdmin(context: {
  supabase: { rpc: (n: string, a: Record<string, unknown>) => PromiseLike<{ data: unknown }> };
  userId: string;
}) {
  const { data } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (!data) throw new Error("Forbidden");
}

export const adminListProductPages = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<ProductPageRow[]> => {
    await assertAdmin(context as never);
    const { data, error } = await context.supabase
      .from("product_page_content")
      .select("handle, draft, published, published_at, updated_at");
    if (error) throw new Error(error.message);
    const byHandle = new Map<string, ProductPageRow>();
    for (const r of (data ?? []) as ProductPageRow[]) byHandle.set(r.handle, r);
    return EDITABLE_HANDLES.map(
      (h) =>
        byHandle.get(h) ?? {
          handle: h,
          draft: {},
          published: null,
          published_at: null,
          updated_at: null,
        },
    );
  });

export const adminSaveProductPageDraft = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { handle: string; draft: unknown }) => data)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const handle = String(data.handle);
    if (!EDITABLE_HANDLES.includes(handle as never)) throw new Error("Unknown product");
    const draft = sanitizeOverride(data.draft);
    const { error } = await context.supabase
      .from("product_page_content")
      .upsert({ handle, draft, updated_at: new Date().toISOString() }, { onConflict: "handle" });
    if (error) throw new Error(error.message);
    return { ok: true as const, draft };
  });

export const adminPublishProductPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { handle: string }) => data)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const handle = String(data.handle);
    const { data: row, error: readErr } = await context.supabase
      .from("product_page_content")
      .select("draft")
      .eq("handle", handle)
      .maybeSingle();
    if (readErr) throw new Error(readErr.message);
    const draft = sanitizeOverride((row as { draft?: unknown } | null)?.draft);
    await publishPrices(handle, draft, (row as any)?.draft ?? null, context.userId, false);
    const { invalidateProductPageCache } = await import("@/lib/product-page.server");
    invalidateProductPageCache();
    return { ok: true as const, published: draft };
  });

export const adminUnpublishProductPage = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data: { handle: string }) => data)
  .handler(async ({ data, context }) => {
    await assertAdmin(context as never);
    const handle = String(data.handle);
    const { data: row, error } = await context.supabase.from("product_page_content").select("draft").eq("handle", handle).maybeSingle();
    if (error) throw new Error(error.message);
    await publishPrices(handle, (row as any)?.draft ?? {}, (row as any)?.draft ?? null, context.userId, true);
    const { invalidateProductPageCache } = await import("@/lib/product-page.server");
    invalidateProductPageCache();
    return { ok: true as const };
  });


async function publishPrices(handle: string, draft: ProductPageOverride, expected: unknown, actor: string, unpublish: boolean) {
  const defaults = PACK_PRICING[handle as SkuHandle];
  if (!defaults) throw new Error("This product uses the standard catalog editor");
  const overrides = unpublish ? {} : packPricingFromOverride(handle, draft);
  const prices = ([1, 2, 3] as const).map(qty => {
    const p = overrides[qty] ?? defaults[qty];
    if (p.mrp < p.price) throw new Error("MRP cannot be below the selling price");
    return { qty, pricePaise: Math.round(p.price * 100), mrpPaise: Math.round(p.mrp * 100) };
  });
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { error } = await (supabaseAdmin as any).rpc("admin_publish_pack_prices", { _handle: handle, _draft: draft, _expected: expected, _prices: prices, _actor: actor, _unpublish: unpublish });
  if (error) throw new Error(error.message);
}
